import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { canAccess } from './utils/accessControl';
import LoginPage from './components/auth/LoginPage';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import Dashboard from './components/dashboard/Dashboard';
import StudentsPage from './components/students/StudentsPage';
import TeachersPage from './components/teachers/TeachersPage';
import AttendancePage from './components/attendance/AttendancePage';
import MarksPage from './components/marks/MarksPage';
import EventsPage from './components/events/EventsPage';
import HomeworkPage from './components/homework/HomeworkPage';
import TimetablePage from './components/timetable/TimetablePage';
import LibraryPage from './components/library/LibraryPage';
import TransportPage from './components/transport/TransportPage';
import AnnouncementsPage from './components/announcements/AnnouncementsPage';
import LeavesPage from './components/leaves/LeavesPage';
import HealthPage from './components/health/HealthPage';
import AchievementsPage from './components/achievements/AchievementsPage';
import AdmissionsPage from './components/admissions/AdmissionsPage';
import ExpensesPage from './components/expenses/ExpensesPage';
import GalleryPage from './components/gallery/GalleryPage';
import VisitorsPage from './components/visitors/VisitorsPage';
import CircularsPage from './components/circulars/CircularsPage';
import FeedbackPage from './components/feedback/FeedbackPage';
import QuestionBankPage from './components/question-bank/QuestionBankPage';
import LessonPlansPage from './components/lesson-plans/LessonPlansPage';
import StudentDiaryPage from './components/student-diary/StudentDiaryPage';
import AnalyticsPage from './components/analytics/AnalyticsPage';
import GeneratorsPage from './components/generators/GeneratorsPage';
import SettingsPage from './components/settings/SettingsPage';
import PlatformLayout from './components/platform/PlatformLayout';
import PlatformDashboard from './components/platform/PlatformDashboard';
import PlatformSchoolsPage from './components/platform/PlatformSchoolsPage';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import DataDeletion from './components/legal/DataDeletion';
import PublicApplyPage from './components/admissions/PublicApplyPage';
import StaffAttendancePage from './components/staff-attendance/StaffAttendancePage';

const pageTitles = {
  '/dashboard': 'Dashboard',
  '/analytics': 'Analytics',
  '/students': 'Students',
  '/teachers': 'Teachers',
  '/attendance': 'Attendance',
  '/marks': 'Marks & Grades',
  '/events': 'Events & Holidays',
  '/homework': 'Homework',
  '/timetable': 'Timetable',
  '/library': 'Library',
  '/transport': 'Transport',
  '/announcements': 'Announcements',
  '/leaves': 'Leave Management',
  '/health': 'Health Records',
  '/achievements': 'Achievements',
  '/admissions': 'Admissions',
  '/expenses': 'Expenses',
  '/staff-attendance': 'Staff Attendance',
  '/gallery': 'Gallery',
  '/visitors': 'Visitor Management',
  '/circulars': 'Circulars & Notices',
  '/feedback': 'Feedback & Complaints',
  '/question-bank': 'Question Bank',
  '/lesson-plans': 'Lesson Plans',
  '/student-diary': 'Student Diary',
  '/generators': 'Document Generators',
  '/settings': 'Settings',
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccess(user.role, location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  const { user } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Legal pages are public — render before any role/auth branching so that
  // /privacy, /terms, /data-deletion always resolve, regardless of whether
  // the visitor is logged out, a tenant user, or the platform owner.
  // Required for Google Play / App Store review (they hit these URLs cold)
  // and for users who want to read policies after signing in.
  // Public routes — render before any role/auth branching so /privacy,
  // /terms, /data-deletion, and the public admissions inquiry form
  // (/apply) always resolve, regardless of session state.
  const isPublicRoute =
    location.pathname === '/privacy' ||
    location.pathname === '/terms' ||
    location.pathname === '/data-deletion' ||
    location.pathname === '/apply';
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/apply" element={<PublicApplyPage />} />
      </Routes>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Superadmin (platform owner) lives in its own shell — no tenant sidebar,
  // no per-school header. Send them to /platform by default.
  if (user.role === 'superadmin') {
    return (
      <Routes>
        <Route path="/platform" element={<PlatformLayout />}>
          <Route index element={<PlatformDashboard />} />
          <Route path="schools" element={<PlatformSchoolsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/platform" replace />} />
      </Routes>
    );
  }

  const pageTitle = pageTitles[location.pathname] || 'Dashboard';

  return (
    <div className="app-layout">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="main-content">
        <Header title={pageTitle} onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        <div className="page-content">
          <Routes>
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/students" element={<ProtectedRoute><StudentsPage /></ProtectedRoute>} />
            <Route path="/teachers" element={<ProtectedRoute><TeachersPage /></ProtectedRoute>} />
            <Route path="/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
            <Route path="/marks" element={<ProtectedRoute><MarksPage /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
            <Route path="/homework" element={<ProtectedRoute><HomeworkPage /></ProtectedRoute>} />
            <Route path="/timetable" element={<ProtectedRoute><TimetablePage /></ProtectedRoute>} />
            <Route path="/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
            <Route path="/transport" element={<ProtectedRoute><TransportPage /></ProtectedRoute>} />
            <Route path="/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
            <Route path="/leaves" element={<ProtectedRoute><LeavesPage /></ProtectedRoute>} />
            <Route path="/health" element={<ProtectedRoute><HealthPage /></ProtectedRoute>} />
            <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
            <Route path="/admissions" element={<ProtectedRoute><AdmissionsPage /></ProtectedRoute>} />
            <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
            <Route path="/staff-attendance" element={<ProtectedRoute><StaffAttendancePage /></ProtectedRoute>} />
            <Route path="/gallery" element={<ProtectedRoute><GalleryPage /></ProtectedRoute>} />
            <Route path="/visitors" element={<ProtectedRoute><VisitorsPage /></ProtectedRoute>} />
            <Route path="/circulars" element={<ProtectedRoute><CircularsPage /></ProtectedRoute>} />
            <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
            <Route path="/question-bank" element={<ProtectedRoute><QuestionBankPage /></ProtectedRoute>} />
            <Route path="/lesson-plans" element={<ProtectedRoute><LessonPlansPage /></ProtectedRoute>} />
            <Route path="/student-diary" element={<ProtectedRoute><StudentDiaryPage /></ProtectedRoute>} />
            <Route path="/generators" element={<ProtectedRoute><GeneratorsPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default App;
